"use client";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCommunitiesListingQuery } from "@/hooks/queries/useCommunitiesListingQuery";
import { useResponsive } from "@/hooks/useResponsive";
import CommunityHomeIcon from "@/icons/community-home";
import { cn } from "@/lib/utils";
import { useCommunityContext } from "@/providers/community-provider";
import { TCommunityJoinedListingRecord, TCommunityListingRecord } from "@/types/community";
import { ChevronsLeftIcon, PlusIcon, TelescopeIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Skeleton from "react-loading-skeleton";

const CommunityNavigation = () => {
	const pathname = usePathname();
	const { isMobile, isTablet } = useResponsive();
	const { isSidebarMinimized, handleMinimizeSidebar, communityInfo } = useCommunityContext();
	const communitySlug = communityInfo?.slug;

	const { data, isLoading } = useCommunitiesListingQuery(communitySlug!, {
		enabled: !!communitySlug,
	});

	const isPublicFeed = pathname === "/communities/feed";
	const isFeedPage = pathname === `/communities/${communitySlug}` || isPublicFeed;

	const routesWithoutSidebar = [
		`/communities/${communitySlug}/home`,
		`/communities/${communitySlug}/calendar`,
		`/communities/${communitySlug}/job-board`,
		`/communities/${communitySlug}/tickets`,
		`/communities/${communitySlug}/products`,
		`/communities/${communitySlug}/vc`,
		`/communities/${communitySlug}/bim`,
	];

	if (routesWithoutSidebar.includes(pathname)) {
		return null;
	}

	const joined =
		(data?.data?.find((item) => item.title === "Joined")
			?.data as TCommunityJoinedListingRecord[]) || [];
	const channels =
		(data?.data?.find((item) => item.title === "Channels")?.data as TCommunityListingRecord[]) ||
		[];
	const communities =
		(data?.data?.find((item) => item.title === "Communities")?.data as TCommunityListingRecord[]) ||
		[];

	return (
		<aside
			dir="rtl"
			className={cn(
				"customized-scrollbar h-screen overflow-y-auto overflow-x-hidden bg-white pb-10 pt-4 shadow-sm",
				isMobile && "hidden",
				isTablet ? "absolute left-0 top-0 z-20" : "",
				isSidebarMinimized ? "w-fit" : "max-w-min md:min-w-[288px] 3xl:min-w-[300px]"
			)}
		>
			<div dir="ltr" className={cn("relative h-full", !isSidebarMinimized && "space-y-5")}>
				<div className="flex flex-col gap-1">
					{isSidebarMinimized ? (
						<TooltipProvider>
							<Tooltip delayDuration={0}>
								<TooltipTrigger>
									<Link
										href={
											communitySlug && !isPublicFeed
												? `/communities/${communitySlug}`
												: "/communities/feed"
										}
										className={cn(
											"flex items-center justify-start gap-2 px-4 py-1.5",
											isFeedPage ? "bg-gray-100" : "bg-transparent"
										)}
									>
										<div className="rounded-lg border  bg-white p-1.5">
											<CommunityHomeIcon className="h-5 w-5 fill-gray-900" />
										</div>
									</Link>
								</TooltipTrigger>
								<TooltipContent side="right" align="start" className="bg-white p-2">
									<span className="text-sm text-gray-700">
										{isPublicFeed ? "Global  Feed" : "Home"}
									</span>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					) : (
						<Link
							href={
								communitySlug && !isPublicFeed
									? `/communities/${communitySlug}`
									: "/communities/feed"
							}
							className={cn(
								"flex items-center justify-start gap-2 px-4 py-1.5",
								isFeedPage ? "bg-gray-100" : "bg-transparent"
							)}
						>
							<div className="rounded-lg border bg-white p-1.5">
								<CommunityHomeIcon className={cn("h-5 w-5 fill-gray-900")} />
							</div>

							<span className="text-sm font-semibold">Home</span>
						</Link>
					)}

					{isSidebarMinimized ? (
						<TooltipProvider>
							<Tooltip delayDuration={0}>
								<TooltipTrigger>
									<Link
										href="/communities"
										className={cn(
											"flex items-center justify-start gap-2 px-4 py-1.5",
											!communitySlug && !isPublicFeed ? "bg-gray-100" : "bg-transparent"
										)}
									>
										<div className="rounded-lg border bg-white p-1.5">
											<TelescopeIcon className="h-5 w-5 fill-gray-900" />
										</div>
									</Link>
								</TooltipTrigger>
								<TooltipContent side="right" align="start" className="bg-white p-2">
									<span className="text-sm text-gray-700">Discover Communities</span>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					) : (
						<Link
							href="/communities"
							className={cn(
								"flex items-center justify-start gap-2 bg-gray-100 px-4 py-1.5",
								!communitySlug && !isPublicFeed ? "bg-gray-100" : "bg-transparent"
							)}
						>
							<div className="rounded-lg border bg-white p-1.5">
								<TelescopeIcon className={cn("h-5 w-5 fill-gray-900")} />
							</div>

							<span className="text-sm font-semibold">Discover Communities</span>
						</Link>
					)}
				</div>

				{communitySlug && (
					<>
						{isSidebarMinimized && <div className="my-3 h-px w-full bg-gray-200" />}

						<ExploreChannels channels={channels} isLoading={isLoading} />

						{isSidebarMinimized && <div className="my-3 h-px w-full bg-gray-200" />}
					</>
				)}

				{isPublicFeed && <JoinedCommunities communities={joined} isLoading={isLoading} />}

				{isPublicFeed && <ExploreCommunities communities={communities} isLoading={isLoading} />}

				<button
					onClick={handleMinimizeSidebar}
					className="sticky bottom-16 left-4 z-30 rounded-md bg-white p-2 text-brand-primary shadow-md"
				>
					<ChevronsLeftIcon
						className={cn(
							"h-6 w-6 transform duration-100 ease-linear",
							isSidebarMinimized && "rotate-180"
						)}
					/>
				</button>
			</div>
		</aside>
	);
};

const CommunityNavigationItem = ({
	href,
	title,
	image,
	unreadPostsCount,
	isMinimized = false,
}: {
	href: string;
	title: string;
	image: string;
	unreadPostsCount?: number;
	isMinimized?: boolean;
}) => {
	const pathname = usePathname();

	const isActive = pathname === href;

	return isMinimized ? (
		<TooltipProvider>
			<Tooltip delayDuration={0}>
				<TooltipTrigger>
					<Link
						href={href}
						className={cn(
							"flex items-center justify-start gap-2 px-4 py-1",
							isActive ? "bg-brand-primary" : "bg-transparent"
						)}
					>
						<Image
							src={image}
							alt={title}
							width={128}
							height={128}
							className="h-8 w-8 rounded-md border"
						/>
					</Link>
				</TooltipTrigger>
				<TooltipContent
					side="right"
					align="start"
					className="flex items-center justify-start gap-1 bg-white p-2"
				>
					<span className="text-sm text-gray-700">{title}</span>
					{unreadPostsCount ? (
						<span className="rounded-md bg-yellow-200 px-2 py-0.5 text-xs font-medium text-yellow-950">
							{unreadPostsCount}
						</span>
					) : null}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	) : (
		<Link
			href={href}
			className={cn(
				"flex items-center justify-start gap-2 px-4 py-1.5",
				isActive ? "bg-brand-primary text-white" : "bg-transparent text-gray-700"
			)}
		>
			<Image
				src={image}
				alt={title}
				width={128}
				height={128}
				className="h-8 w-8 rounded-md border"
			/>

			<span className="text-sm">{title}</span>

			{unreadPostsCount ? (
				<span
					className={cn("rounded-md bg-yellow-200 px-2 py-0.5 text-xs font-medium text-yellow-950")}
				>
					{unreadPostsCount}
				</span>
			) : null}
		</Link>
	);
};

const JoinedCommunities = ({
	isLoading,
	communities,
}: {
	isLoading?: boolean;
	communities?: TCommunityJoinedListingRecord[];
}) => {
	const { isSidebarMinimized } = useCommunityContext();

	return isSidebarMinimized ? (
		<div className="grid gap-px">
			{isLoading ? (
				<div className="mx-auto p-4">
					<Skeleton className="h-8 w-8 md:h-10 md:w-10" circle />
				</div>
			) : communities && communities.length > 0 ? (
				communities.map((community) => (
					<CommunityNavigationItem
						isMinimized
						key={community.slug}
						title={community.name}
						href={`/communities/${community.slug}`}
						unreadPostsCount={community.unreadCount}
						image={community.logo || "/images/default-placeholder-image.png"}
					/>
				))
			) : null}
		</div>
	) : (
		<div>
			<h3 className="mb-2 px-4 text-sm font-semibold text-gray-600">Joined Communities</h3>

			{isLoading ? (
				<div className="flex items-center justify-start gap-2 p-4">
					<Skeleton className="h-6 w-6 md:h-10 md:w-10" circle />
					<Skeleton className="h-6 w-48 md:h-10" />
				</div>
			) : communities && communities.length > 0 ? (
				communities.map((community) => (
					<CommunityNavigationItem
						key={community.slug}
						title={community.name}
						href={`/communities/${community.slug}`}
						unreadPostsCount={community.unreadCount}
						image={community.logo || "/images/default-placeholder-image.png"}
					/>
				))
			) : null}
		</div>
	);
};

const ExploreChannels = ({
	isLoading,
	channels,
}: {
	isLoading?: boolean;
	channels?: TCommunityListingRecord[];
}) => {
	const { isSidebarMinimized, communityInfo } = useCommunityContext();
	const communitySlug = communityInfo?.slug;

	return isSidebarMinimized ? (
		<div className="grid gap-px">
			{isLoading ? (
				<div className="mx-auto p-4">
					<Skeleton className="h-8 w-8 md:h-10 md:w-10" circle />
				</div>
			) : channels && channels.length > 0 ? (
				channels.map((channel) => (
					<CommunityNavigationItem
						isMinimized
						key={channel.slug}
						title={channel.name}
						image={channel.logo}
						href={`/communities/${communitySlug}/${channel.slug}`}
					/>
				))
			) : null}
		</div>
	) : (
		<div>
			<h3 className="mb-2 px-4 text-sm font-semibold text-gray-600">Channels</h3>

			{isLoading ? (
				<div className="flex items-center justify-start gap-2 p-4">
					<Skeleton className="h-6 w-6 md:h-10 md:w-10" circle />
					<Skeleton className="h-6 w-48 md:h-10" />
				</div>
			) : channels && channels.length > 0 ? (
				channels.map((channel) => (
					<CommunityNavigationItem
						key={channel.slug}
						title={channel.name}
						image={channel.logo}
						isMinimized={isSidebarMinimized}
						href={`/communities/${communitySlug}/${channel.slug}`}
					/>
				))
			) : (
				<span className="px-4 text-xs text-gray-400">No channels joined</span>
			)}
		</div>
	);
};

const ExploreCommunities = ({
	isLoading,
	communities,
}: {
	isLoading?: boolean;
	communities?: TCommunityListingRecord[];
}) => {
	const { isSidebarMinimized } = useCommunityContext();

	return isSidebarMinimized ? (
		<div className="grid gap-px">
			{isLoading ? (
				<div className="mx-auto p-4">
					<Skeleton className="h-8 w-8 md:h-10 md:w-10" circle />
				</div>
			) : communities && communities.length > 0 ? (
				communities.map((community) => (
					<CommunityNavigationItem
						isMinimized
						key={community.slug}
						title={community.name}
						image={community.logo}
						href={`/communities/${community.slug}`}
					/>
				))
			) : null}
		</div>
	) : (
		<div>
			<h3 className="mb-2 px-4 text-sm font-semibold text-gray-600">Communities</h3>

			<div>
				{isLoading ? (
					<div className="flex items-center justify-start gap-2 p-4">
						<Skeleton className="h-6 w-6 md:h-10 md:w-10" circle />
						<Skeleton className="h-6 w-48 md:h-10" />
					</div>
				) : communities && communities.length > 0 ? (
					communities.map((community) => (
						<CommunityNavigationItem
							key={community.slug}
							title={community.name}
							image={community.logo}
							href={`/communities/${community.slug}`}
						/>
					))
				) : (
					<span className="px-4 text-xs text-gray-400">No communities joined</span>
				)}
			</div>

			<div className="px-4">
				<Link
					href="/contact-us"
					className="mt-3 flex gap-1 rounded-md border bg-gradient-to-r from-[#EFFEF4] to-[#FCEEFD] px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
				>
					<PlusIcon className="h-5 w-5" />

					<span>Create your own community</span>
				</Link>
			</div>
		</div>
	);
};

export default CommunityNavigation;
